import React from 'react';
import { Activity, TrendingUp, BarChart2 } from 'lucide-react';
import { METHODOLOGY_ITEMS, RISK_MANAGEMENT_TEXT } from '../constants';
import { IconName } from '../types';

const Features: React.FC = () => {
  const getIcon = (iconName: IconName) => {
    switch(iconName) {
      case 'Activity': return <Activity strokeWidth={1} size={28} className="text-sage-400" />;
      case 'TrendingUp': return <TrendingUp strokeWidth={1} size={28} className="text-sage-400" />;
      case 'BarChart2': return <BarChart2 strokeWidth={1} size={28} className="text-sage-400" />;
      default: return <Activity strokeWidth={1} size={28} className="text-sage-400" />;
    }
  };

  return (
    <>
      {/* Methodology Section - Architectural Grid */}
      <section id="methodology" className="bg-slate-950 border-b border-slate-900 scroll-mt-28">
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-900 border-b border-slate-900">
          
          {METHODOLOGY_ITEMS.map((item, index) => (
            <div key={index} className="p-12 md:p-16 hover:bg-slate-900/20 transition-colors group">
              <div className="mb-8 opacity-80 group-hover:opacity-100 transition-opacity">
                {getIcon(item.icon)}
              </div>
              <h3 className="text-lg font-medium text-white mb-4 tracking-wide uppercase">{item.title}</h3>
              <p className="text-slate-500 font-light leading-relaxed text-sm">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Risk Management Section - Split Layout */}
      <section className="py-0 bg-slate-950 relative border-b border-slate-900">
        <div className="grid md:grid-cols-2">
          
          {/* Content Side */}
          <div className="p-12 md:p-24 border-b md:border-b-0 md:border-r border-slate-900 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-6">
               <span className="w-8 h-[1px] bg-sage-400"></span>
               <span className="text-sage-400 text-xs font-mono tracking-widest uppercase">Risk Protocol</span>
            </div>
            
            <h2 className="text-3xl md:text-5xl font-light text-white mb-8">
              {RISK_MANAGEMENT_TEXT.title}
            </h2>
            <p className="text-slate-400 mb-12 font-light leading-relaxed">
              {RISK_MANAGEMENT_TEXT.description}
            </p>
            <ul className="space-y-6">
              {RISK_MANAGEMENT_TEXT.points.map((point, idx) => (
                <li key={idx} className="flex items-start gap-4 group">
                  <div className="mt-1.5 w-1.5 h-1.5 bg-slate-800 group-hover:bg-sage-400 transition-colors"></div>
                  <span className="text-slate-400 font-light text-sm">{point}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Visual Side - Abstract Representation */}
          <div className="bg-slate-925 p-12 md:p-24 flex items-center justify-center relative overflow-hidden">
            {/* Background Lines */}
            <div className="absolute inset-0 bg-[size:20px_20px] bg-grid-pattern opacity-10"></div>
            
            <div className="w-full max-w-md space-y-12 relative z-10">
                
                {/* Metric 1 */}
                <div className="space-y-3">
                    <div className="flex justify-between text-xs font-mono text-slate-500 uppercase tracking-wider">
                        <span>Stake Plano</span>
                        <span className="text-sage-400">Recomendado</span>
                    </div>
                    <div className="w-full bg-slate-900 h-1">
                        <div className="bg-sage-400 h-1 w-1/4"></div>
                    </div>
                    <div className="text-right text-[10px] text-slate-600 font-mono">VAR: LOW</div>
                </div>

                {/* Metric 2 */}
                <div className="space-y-3">
                    <div className="flex justify-between text-xs font-mono text-slate-500 uppercase tracking-wider">
                        <span>Gestión Variable</span>
                        <span className="text-slate-600">Alto Riesgo</span>
                    </div>
                    <div className="w-full bg-slate-900 h-1">
                        <div className="bg-slate-700 h-1 w-1/2"></div>
                    </div>
                    <div className="text-right text-[10px] text-slate-600 font-mono">VAR: HIGH</div>
                </div>

                <div className="pt-8 border-t border-slate-800 mt-8">
                    <p className="text-xs text-slate-500 font-mono leading-relaxed">
                        "El objetivo principal no es solo ganar la siguiente apuesta, sino asegurar que el bank siga creciendo mes a mes."
                    </p>
                </div>

            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Features;